<template>
  <div class="relative">
    <div v-if="loading" class="absolute inset-0 z-15 w-full flex items-center justify-center bg-gray-100 opacity-75">
      <f-loading show>
        <template
          v-if="slots.loading" 
          #text
        >
          <slot name="loading"></slot>
        </template>
      </f-loading>  
    </div>
    <table 
      class="w-full border shadow-sm mb-4"
      :class="classes"
    >
      <thead>
        <f-tr>
          <slot 
            name="header" 
          />
          <f-th scope="col" class="text-right"></f-th>
        </f-tr>
      </thead>
      <tbody>
        <template v-if="items?.length > 0">
          <template 
            v-for="(item, index) in items"
            :key="getKeyValue(item, itemKey, index)"
          >
            <f-tr>
              <slot
                name="row"
                :item="item"
                :index="index"
              />
              <f-td
                class="text-right"
              >
                <button v-if="!loading" @click="() => onToggleDetailsClick(getKeyValue(item, itemKey as keyof T, index))">
                  <font-awesome-icon 
                    :icon="['far', 'angle-up']"
                    class="transition-transform duration-300"
                    fixed-width
                    size="xl"
                    :class="[{'rotate-0': openedDetails.includes(getKeyValue(item, itemKey as keyof T, index)), 'rotate-180': !openedDetails.includes(getKeyValue(item, itemKey as keyof T, index))}]"
                  >
                  </font-awesome-icon>
                </button>
              </f-td>
            </f-tr>
            <f-tr
              v-if="openedDetails.includes(getKeyValue(item, itemKey as keyof T, index))"
            >
              <f-td colspan="100%">
                <slot
                  name="details"
                  :item="item"
                  :index="index"
                />
              </f-td>
            </f-tr>  
          </template>
        </template>
        <template v-else-if="slots.empty">
          <slot name="empty"></slot>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, ref, useSlots, type Ref } from 'vue';

export interface FTableProps<T> {
  items: ReadonlyArray<T>;
  itemKey: keyof T;
  loading?: boolean;
}

const { loading = false } = defineProps<FTableProps<T>>();

const openedDetails: Ref<Array<string | number>> = ref([]);

const onToggleDetailsClick = (key: string | number) => {
  if (openedDetails.value.includes(key)) {
    openedDetails.value.splice(openedDetails.value.findIndex(r => r === key), 1);
  } else {
    openedDetails.value.push(key);
  }
};

const getKeyValue = (item: T, key: keyof T, index: number) : string => {
  const keys = (key as string).split('.');
  if (keys.length < 2) {
    return item[key]?.toString() ?? index.toString();
  }

  let obj = item as object;
  for (let i = 0; i < keys.length; i++) {
    if (obj == null || typeof obj !== 'object') {
      return index.toString();
    }

    obj = obj[keys[i] as keyof object] as object;
  }

  return obj?.toString();
};

const slots = useSlots();

const classes = computed(() => {
  if (!loading) {
    return [];
  }

  return ['pointer-events-none','select-none'];
});
</script>